import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, ShieldCheck, RotateCcw, Users as UsersIcon, Settings as SettingsIcon, Building2, Phone, QrCode, Upload, Copy, Trash2, Printer, Download, UserPlus, Plus, Pencil, Check, X, Plug, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { maskPhone } from "@/lib/masks";
import { PageHeader } from "@/components/PageHeader";
import { TotemLinks } from "@/components/TotemLinks";

// Módulos disponíveis (o `key` bate com a rota do sidebar)
const MODULES = [
  { key: "/", label: "Dashboard" },
  { key: "/consolidado", label: "Consolidado (multi-unidades)", adminOnly: true },
  { key: "/venda-rapida", label: "Venda Rápida" },
  { key: "/alunos", label: "Alunos" },
  { key: "/mapa-alunos", label: "Mapa de Alunos" },
  { key: "/planos", label: "Planos" },
  { key: "/treinos", label: "Treinos" },
  { key: "/biblioteca-exercicios", label: "Biblioteca de Exercícios" },
  { key: "/catraca", label: "Catraca" },
  { key: "/financeiro", label: "Financeiro" },
  { key: "/caixa-dia", label: "Caixa do Dia" },
  { key: "/balanco-mensal", label: "Balanço Mensal" },
  { key: "/pdv", label: "Loja / PDV" },
  { key: "/avisos", label: "Avisos (Portal)" },
  { key: "/feedback-alunos", label: "Feedback Alunos" },
  { key: "/site-vendas", label: "Site de Vendas" },
  { key: "/relatorios", label: "Relatórios" },
  { key: "/dispositivos", label: "Dispositivos", adminOnly: true },
  { key: "/unidades", label: "Unidades", adminOnly: true },
  { key: "/usuarios", label: "Usuários", adminOnly: true },
  { key: "/avaliacoes", label: "Avaliações Funcionários", adminOnly: true },
  { key: "/configuracoes", label: "Configurações", adminOnly: true },
];

// Perfis padrão sugeridos por role — usado ao clicar em "Aplicar padrão do perfil"
const ROLE_DEFAULTS = {
  reception: ["/", "/venda-rapida", "/alunos", "/mapa-alunos", "/planos", "/caixa-dia", "/pdv", "/catraca"],
  trainer: ["/", "/alunos", "/treinos", "/biblioteca-exercicios"],
  finance: ["/", "/financeiro", "/caixa-dia", "/balanco-mensal", "/planos"],
  admin: MODULES.map(m => m.key),
};

// Permissões granulares (ações específicas dentro dos módulos)
const ACTION_PERMS = [
  { key: "sales.delete", label: "Excluir venda do caixa", hint: "Permite ao usuário remover lançamentos e reverter dias do plano (Admin/Finance já têm por padrão)" },
  { key: "cash.open_close", label: "Fechar e reabrir caixa do dia", hint: "Permite fechar o caixa diário e reabri-lo para novos lançamentos (Admin/Finance já têm por padrão)" },
  { key: "attendance.view", label: "Consultar e exportar presenças do aluno", hint: "Mostra a aba Presenças no cadastro do aluno, com PDF, impressão e WhatsApp (Administrador já tem por padrão)" },
];

const ROLE_LABEL = { admin: "Administrador", reception: "Recepção", trainer: "Personal", finance: "Financeiro" };
const ROLE_COLOR = { admin: "#E11D48", reception: "#3B82F6", trainer: "#10B981", finance: "#F59E0B" };

export default function Settings() {
  const [users, setUsers] = useState([]);
  const [perms, setPerms] = useState({}); // userId -> [modulos]
  const [dirty, setDirty] = useState({}); // userId -> boolean
  const [saving, setSaving] = useState({}); // userId -> boolean
  const [adminCfg, setAdminCfg] = useState({ admin_name: "", admin_phone: "", pix_qr_image: "", pix_code: "", pix_key: "" });
  const [adminSaving, setAdminSaving] = useState(false);
  // Role permissions (per-profile defaults)
  const [rolePerms, setRolePerms] = useState({ reception: [], trainer: [], finance: [] });
  const [roleDirty, setRoleDirty] = useState(false);
  const [roleSaving, setRoleSaving] = useState(false);
  const [customRoles, setCustomRoles] = useState([]); // [{key,label}]
  const [newRoleLabel, setNewRoleLabel] = useState("");
  const [creatingRole, setCreatingRole] = useState(false);
  const [renamingRole, setRenamingRole] = useState(null); // {key,label} being renamed
  const [renameValue, setRenameValue] = useState("");

  const loadAdmin = async () => {
    try { const { data } = await api.get("/admin-config"); setAdminCfg({ admin_name: data.admin_name || "", admin_phone: data.admin_phone || "", pix_qr_image: data.pix_qr_image || "", pix_code: data.pix_code || "", pix_key: data.pix_key || "" }); } catch {}
  };
  const uploadPixQr = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("Imagem muito grande (máx 3MB)");
    const r = new FileReader();
    r.onload = () => setAdminCfg((p)=>({ ...p, pix_qr_image: r.result }));
    r.readAsDataURL(f);
  };
  const saveAdmin = async () => {
    setAdminSaving(true);
    try {
      await api.patch("/admin-config", adminCfg);
      toast.success("Configuração do admin salva");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setAdminSaving(false); }
  };

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
    const p = {};
    data.forEach(u => { p[u.id] = u.permissions || []; });
    setPerms(p);
    setDirty({});
  };
  useEffect(() => { load(); loadAdmin(); loadRolePerms(); loadCustomRoles(); }, []);

  const loadCustomRoles = async () => {
    try { const { data } = await api.get("/custom-roles"); setCustomRoles(data || []); } catch {}
  };
  const createCustomRole = async () => {
    const label = newRoleLabel.trim();
    if (!label) return toast.error("Informe o nome do perfil");
    const key = label.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    if (!key) return toast.error("Nome inválido");
    setCreatingRole(true);
    try {
      await api.post("/custom-roles", { key, label });
      toast.success(`Perfil "${label}" criado`);
      setNewRoleLabel("");
      await loadCustomRoles();
      // Ensure the new role appears in the matrix (empty perms by default)
      setRolePerms(prev => ({ ...prev, [key]: prev[key] || [] }));
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setCreatingRole(false); }
  };
  const deleteCustomRole = async (key, label) => {
    if (!window.confirm(`Remover o perfil "${label}"? Usuários com esse perfil precisam ser trocados antes.`)) return;
    try {
      await api.delete(`/custom-roles/${key}`);
      toast.success("Perfil removido");
      await loadCustomRoles();
      setRolePerms(prev => { const n = { ...prev }; delete n[key]; return n; });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const startRenameRole = (r) => { setRenamingRole(r); setRenameValue(r.label); };
  const cancelRename = () => { setRenamingRole(null); setRenameValue(""); };
  const confirmRename = async () => {
    const label = renameValue.trim();
    if (!label) return toast.error("Nome inválido");
    if (label === renamingRole.label) return cancelRename();
    try {
      await api.patch(`/custom-roles/${renamingRole.key}`, { label });
      toast.success("Perfil renomeado");
      await loadCustomRoles();
      cancelRename();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const loadRolePerms = async () => {
    try {
      const { data } = await api.get("/role-permissions");
      setRolePerms({
        reception: data.reception || [],
        trainer: data.trainer || [],
        finance: data.finance || [],
      });
      setRoleDirty(false);
    } catch (e) { /* non-admin */ }
  };
  const toggleRolePerm = (role, key) => {
    setRolePerms(prev => {
      const cur = prev[role] || [];
      const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
      return { ...prev, [role]: next };
    });
    setRoleDirty(true);
  };
  const applyRoleDefault = (role) => {
    setRolePerms(prev => ({ ...prev, [role]: [...(ROLE_DEFAULTS[role] || [])] }));
    setRoleDirty(true);
    toast.info(`Padrão original de ${ROLE_LABEL[role]} carregado`);
  };
  const saveRolePerms = async () => {
    setRoleSaving(true);
    try {
      await api.patch("/role-permissions", rolePerms);
      toast.success("Permissões dos perfis salvas — vale para todos os usuários sem permissão customizada");
      setRoleDirty(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setRoleSaving(false); }
  };

  const toggle = (uid, key) => {
    setPerms(prev => {
      const cur = prev[uid] || [];
      const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
      return { ...prev, [uid]: next };
    });
    setDirty(prev => ({ ...prev, [uid]: true }));
  };

  const applyDefault = (uid, role) => {
    const defaults = ROLE_DEFAULTS[role] || [];
    setPerms(prev => ({ ...prev, [uid]: [...defaults] }));
    setDirty(prev => ({ ...prev, [uid]: true }));
    toast.info(`Padrão de ${ROLE_LABEL[role]} carregado — clique em Salvar para aplicar`);
  };

  const save = async (uid) => {
    setSaving(prev => ({ ...prev, [uid]: true }));
    try {
      await api.patch(`/users/${uid}`, { permissions: perms[uid] });
      toast.success("Permissões salvas");
      setDirty(prev => ({ ...prev, [uid]: false }));
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(prev => ({ ...prev, [uid]: false })); }
  };

  const saveAll = async () => {
    const dirtyIds = Object.entries(dirty).filter(([, v]) => v).map(([k]) => k);
    if (dirtyIds.length === 0) return toast.info("Nada para salvar");
    let ok = 0;
    for (const uid of dirtyIds) {
      try { await api.patch(`/users/${uid}`, { permissions: perms[uid] }); ok++; } catch {}
    }
    toast.success(`${ok}/${dirtyIds.length} usuário(s) atualizado(s)`);
    load();
  };

  const nonAdmin = users.filter(u => u.role !== "admin");
  const dirtyCount = Object.values(dirty).filter(Boolean).length;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Configurações"
        subtitle="Defina permissões por perfil, por usuário e configure QR Codes e dados do administrador"
        actions={
          dirtyCount > 0 && (
            <Button onClick={saveAll} className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" data-testid="settings-save-all">
              <Save className="w-4 h-4 mr-2"/>Salvar todas ({dirtyCount})
            </Button>
          )
        }
      />

      <Tabs defaultValue="perfis" className="w-full">
        <TabsList data-testid="settings-tabs">
          <TabsTrigger value="perfis" data-testid="settings-tab-perfis"><ShieldCheck className="w-4 h-4 mr-2"/>Perfis</TabsTrigger>
          <TabsTrigger value="usuarios" data-testid="settings-tab-users"><UsersIcon className="w-4 h-4 mr-2"/>Usuários</TabsTrigger>
          <TabsTrigger value="qrcodes" data-testid="settings-tab-qr"><QrCode className="w-4 h-4 mr-2"/>QR Codes</TabsTrigger>
          <TabsTrigger value="totem" data-testid="settings-tab-totem"><QrCode className="w-4 h-4 mr-2"/>Totem</TabsTrigger>
          <TabsTrigger value="admin" data-testid="settings-tab-admin"><Building2 className="w-4 h-4 mr-2"/>Admin & Pix</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="settings-tab-integrations"><Plug className="w-4 h-4 mr-2"/>Integrações</TabsTrigger>
        </TabsList>

        {/* ===================== TAB: PERFIS ===================== */}
        <TabsContent value="perfis" className="space-y-4">
          <Card className="p-5 border-slate-200" data-testid="role-perms-card">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-rose-600"/>
                  <h3 className="font-heading text-base font-bold text-slate-900">Permissões por Perfil (Role)</h3>
                </div>
                <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                  Marque os módulos que cada perfil pode acessar. Vale para <strong>todos os usuários</strong> desse perfil que <strong>não têm permissões customizadas</strong> na aba Usuários. Admin sempre tem acesso a tudo.
                </p>
              </div>
              {roleDirty && (
                <Button onClick={saveRolePerms} disabled={roleSaving} className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" data-testid="save-role-perms">
                  <Save className="w-4 h-4 mr-2"/>{roleSaving ? "Salvando..." : "Salvar perfis"}
                </Button>
              )}
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {[
                { key: "reception", label: ROLE_LABEL.reception, color: ROLE_COLOR.reception, removable: false },
                { key: "trainer", label: ROLE_LABEL.trainer, color: ROLE_COLOR.trainer, removable: false },
                { key: "finance", label: ROLE_LABEL.finance, color: ROLE_COLOR.finance, removable: false },
                ...customRoles.map(r => ({ key: r.key, label: r.label, color: "#8B5CF6", removable: true })),
              ].map(({ key: role, label: roleLabel, color: roleColor, removable }) => {
                const list = rolePerms[role] || [];
                return (
                  <div key={role} className="rounded-xl border-2 border-slate-200 overflow-hidden" data-testid={`role-perms-${role}`}>
                    <div className="px-4 py-3 flex items-center justify-between" style={{ background: `${roleColor}15`, borderBottom: `2px solid ${roleColor}` }}>
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="w-3 h-3 rounded-full shrink-0" style={{ background: roleColor }}/>
                        {renamingRole?.key === role ? (
                          <Input
                            value={renameValue}
                            onChange={(e)=>setRenameValue(e.target.value)}
                            onKeyDown={(e)=>{ if (e.key === "Enter") confirmRename(); if (e.key === "Escape") cancelRename(); }}
                            autoFocus
                            className="h-7 text-sm py-0 px-2 font-heading font-extrabold uppercase"
                            data-testid={`role-rename-input-${role}`}
                          />
                        ) : (
                          <span className="font-heading font-extrabold text-sm text-slate-900 uppercase truncate">{roleLabel}</span>
                        )}
                        {removable && renamingRole?.key !== role && <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-bold uppercase shrink-0">Custom</span>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {renamingRole?.key === role ? (
                          <>
                            <Button variant="ghost" size="sm" onClick={confirmRename} title="Confirmar" data-testid={`role-rename-confirm-${role}`} className="h-7 px-2 text-emerald-600 hover:text-emerald-700"><Check className="w-4 h-4"/></Button>
                            <Button variant="ghost" size="sm" onClick={cancelRename} title="Cancelar" data-testid={`role-rename-cancel-${role}`} className="h-7 px-2 text-slate-500"><X className="w-4 h-4"/></Button>
                          </>
                        ) : (
                          <>
                            <span className="text-[10px] font-bold text-slate-500 uppercase">{list.length}</span>
                            {!removable && (
                              <Button variant="ghost" size="sm" onClick={()=>applyRoleDefault(role)} title="Voltar ao padrão original" data-testid={`role-reset-${role}`} className="h-7 px-2 text-[10px]"><RotateCcw className="w-3 h-3"/></Button>
                            )}
                            {removable && (
                              <>
                                <Button variant="ghost" size="sm" onClick={()=>startRenameRole({ key: role, label: roleLabel })} title="Renomear perfil" data-testid={`role-rename-${role}`} className="h-7 px-2 text-[10px] text-sky-600 hover:text-sky-700"><Pencil className="w-3 h-3"/></Button>
                                <Button variant="ghost" size="sm" onClick={()=>deleteCustomRole(role, roleLabel)} title="Excluir perfil" data-testid={`role-delete-${role}`} className="h-7 px-2 text-[10px] text-rose-500 hover:text-rose-700"><Trash2 className="w-3 h-3"/></Button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="p-3 space-y-1 max-h-[420px] overflow-y-auto">
                      {MODULES.filter(m => !m.adminOnly).map(m => {
                        const checked = list.includes(m.key);
                        return (
                          <label key={m.key} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-colors ${checked ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
                            <Checkbox checked={checked} onCheckedChange={()=>toggleRolePerm(role, m.key)} data-testid={`role-${role}-${m.key}`}/>
                            <span className="text-slate-800 font-medium">{m.label}</span>
                          </label>
                        );
                      })}
                      {ACTION_PERMS.length > 0 && (
                        <div className="pt-2 mt-2 border-t border-dashed border-slate-200">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold px-2 mb-1">Ações específicas</div>
                          {ACTION_PERMS.map(a => {
                            const checked = list.includes(a.key);
                            return (
                              <label key={a.key} className={`flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-colors ${checked ? "bg-rose-50" : "hover:bg-slate-50"}`}>
                                <Checkbox checked={checked} onCheckedChange={()=>toggleRolePerm(role, a.key)} data-testid={`role-${role}-${a.key}`} className="mt-0.5"/>
                                <div>
                                  <div className="font-semibold text-slate-800">{a.label}</div>
                                  <div className="text-[10px] text-slate-500 leading-tight">{a.hint}</div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Create custom role */}
            <div className="mt-4 p-4 rounded-xl border-2 border-dashed border-violet-300 bg-violet-50/40" data-testid="create-role-card">
              <div className="flex items-center gap-2 mb-2">
                <Plus className="w-4 h-4 text-violet-600"/>
                <h4 className="font-heading text-sm font-bold text-slate-900 uppercase">Criar perfil customizado</h4>
                <span className="text-[11px] text-slate-500">Ex: Gerente, Estagiário, Nutricionista</span>
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Nome do perfil</Label>
                  <Input value={newRoleLabel} onChange={(e)=>setNewRoleLabel(e.target.value)} placeholder="Ex: Gerente Regional" data-testid="new-role-input"/>
                </div>
                <Button onClick={createCustomRole} disabled={creatingRole || !newRoleLabel.trim()} className="bg-violet-600 hover:bg-violet-700 font-bold uppercase" data-testid="create-role-btn">
                  <Plus className="w-4 h-4 mr-2"/>{creatingRole ? "Criando..." : "Criar perfil"}
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Após criar, marque os módulos que esse perfil pode acessar no card à direita, salve e cadastre usuários com esse perfil na aba <strong>Usuários</strong>.</p>
            </div>

            <div className="mt-4 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-slate-400 shrink-0 mt-0.5"/>
              <div>
                <strong className="text-slate-700">Como funciona:</strong> Ao logar, um usuário sem permissões próprias herda automaticamente as permissões do perfil dele. Se você marcar módulos direto na aba <strong>Usuários</strong> para alguém específico, essas permissões pessoais têm prioridade sobre o perfil.
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* ===================== TAB: USUÁRIOS ===================== */}
        <TabsContent value="usuarios" className="space-y-4">
          <Card className="border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <UsersIcon className="w-4 h-4 text-slate-600"/>
              <h3 className="font-heading font-bold text-slate-900">Permissões por Usuário</h3>
              <span className="text-xs text-slate-500">({nonAdmin.length} usuário{nonAdmin.length !== 1 ? "s" : ""} — admin oculto · sobrepõe permissões do perfil)</span>
            </div>

            {nonAdmin.length === 0 ? (
              <div className="p-12 text-center text-slate-500">
                <ShieldCheck className="w-12 h-12 text-slate-300 mx-auto mb-2"/>
                Nenhum usuário além de administrador cadastrado.
              </div>
            ) : (
              <div className="divide-y divide-slate-100" data-testid="settings-users-list">
                {nonAdmin.map(u => {
                  const userPerms = perms[u.id] || [];
                  const isDirty = dirty[u.id];
                  return (
                    <div key={u.id} className="p-5 hover:bg-slate-50/50 transition-colors" data-testid={`settings-user-${u.id}`}>
                      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shrink-0" style={{ background: ROLE_COLOR[u.role] || "#64748b" }}>
                            {u.name[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="font-heading font-bold text-slate-900">{u.name}</div>
                            <div className="text-xs text-slate-500">{u.email} · <span className="font-semibold" style={{ color: ROLE_COLOR[u.role] }}>{ROLE_LABEL[u.role]}</span>
                              {userPerms.length === 0 && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-bold uppercase">Herda do perfil</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">{userPerms.length} módulos</span>
                          <Button variant="outline" size="sm" onClick={()=>applyDefault(u.id, u.role)} data-testid={`apply-default-${u.id}`}><RotateCcw className="w-3 h-3 mr-1"/>Padrão do perfil</Button>
                          <Button size="sm" onClick={()=>save(u.id)} disabled={!isDirty || saving[u.id]} className={isDirty ? "bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" : "bg-slate-300 text-slate-500 font-bold uppercase"} data-testid={`save-user-${u.id}`}>
                            <Save className="w-3 h-3 mr-1"/>{saving[u.id] ? "..." : (isDirty ? "Salvar" : "Salvo")}
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 pl-13">
                        {MODULES.filter(m => !m.adminOnly).map(m => {
                          const checked = userPerms.includes(m.key);
                          return (
                            <label key={m.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer border transition-all ${checked ? "bg-emerald-50 border-emerald-200 hover:bg-emerald-100" : "bg-slate-50 border-slate-200 hover:bg-slate-100"}`}>
                              <Checkbox checked={checked} onCheckedChange={()=>toggle(u.id, m.key)} data-testid={`perm-${u.id}-${m.key}`}/>
                              <span className="text-xs font-medium text-slate-800 truncate">{m.label}</span>
                            </label>
                          );
                        })}
                      </div>

                      {u.role !== "finance" && (
                        <div className="mt-3 pt-3 border-t border-dashed border-slate-200">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Ações específicas</div>
                          <div className="grid gap-1.5">
                            {ACTION_PERMS.map(a => {
                              const checked = userPerms.includes(a.key);
                              return (
                                <label key={a.key} className={`flex items-start gap-2 px-3 py-2 rounded-lg cursor-pointer border transition-all ${checked ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200 hover:bg-slate-100"}`}>
                                  <Checkbox checked={checked} onCheckedChange={()=>toggle(u.id, a.key)} data-testid={`action-${u.id}-${a.key}`} className="mt-0.5"/>
                                  <div>
                                    <div className="text-xs font-bold text-slate-900">{a.label}</div>
                                    <div className="text-[10px] text-slate-500">{a.hint}</div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card className="p-4 border-slate-200 bg-slate-50/50">
            <div className="text-xs text-slate-600 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-slate-400 shrink-0 mt-0.5"/>
              <div>
                <strong>Regra de aplicação:</strong> permissões marcadas aqui sobrescrevem o perfil do usuário. Se nada estiver marcado, o padrão do perfil (aba <em>Perfis</em>) é aplicado automaticamente no login.
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* ===================== TAB: QR CODES ===================== */}
        <TabsContent value="qrcodes" className="space-y-4">
          {/* ==== QR AUTOCADASTRO DE NOVO ALUNO ==== */}
          <Card className="p-5 border-slate-200" data-testid="signup-qr-card">
            <div className="flex items-center gap-2 mb-4">
              <UserPlus className="w-5 h-5 text-rose-600"/>
              <h3 className="font-heading text-base font-bold text-slate-900">QR Code — Autocadastro de Novo Aluno</h3>
              <span className="text-xs text-slate-500">Aluno escaneia, preenche dados, tira foto e paga via Pix — sem sair da recepção</span>
            </div>
            {(() => {
              const url = `${window.location.origin}/autocadastro`;
              const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=10&data=${encodeURIComponent(url)}`;
              const doPrint = () => {
                const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Autocadastro — American Fit</title>
                  <style>@page{size:A4;margin:0}html,body{margin:0;padding:0}
                  body{font-family:'Segoe UI',Arial,sans-serif;width:210mm;min-height:297mm;padding:18mm 15mm;text-align:center;box-sizing:border-box;background:#fff;color:#0f172a}
                  .badge{display:inline-block;background:#E11D48;color:#fff;font-size:13px;font-weight:800;letter-spacing:3px;padding:8px 18px;border-radius:999px;text-transform:uppercase;margin-bottom:12px}
                  h1{font-size:56px;letter-spacing:2px;margin:0 0 6px;font-weight:900;text-transform:uppercase}
                  .sub{font-size:20px;color:#475569;margin:0 0 22px;font-weight:600}
                  .qr-wrap{display:inline-block;padding:16px;border:6px solid #E11D48;border-radius:24px;background:#fff}
                  img{display:block;width:145mm;height:145mm}
                  .cta{margin-top:28px;font-size:32px;font-weight:900;color:#0f172a;text-transform:uppercase;letter-spacing:1px}
                  .cta em{font-style:normal;color:#E11D48}
                  .steps{margin-top:18px;font-size:15px;color:#334155;line-height:1.7;text-align:left;max-width:150mm;margin-left:auto;margin-right:auto}
                  .steps b{color:#0f172a}
                  .foot{margin-top:28px;padding-top:14px;border-top:2px dashed #cbd5e1;font-size:11px;color:#64748b;word-break:break-all}
                  @media print{body{padding:12mm 10mm}}</style></head><body>
                  <div class="badge">Matricule-se agora</div>
                  <h1>American Fit</h1>
                  <div class="sub">Comece a treinar em 3 minutos — cadastro rápido pelo seu celular</div>
                  <div class="qr-wrap"><img src="${qrUrl}" alt="QR Autocadastro"/></div>
                  <div class="cta">Aponte a câmera e <em>faça sua matrícula</em></div>
                  <div class="steps">
                    <div><b>1.</b> Abra a câmera do celular e mire no QR Code acima</div>
                    <div><b>2.</b> Toque no link e preencha seus dados + endereço</div>
                    <div><b>3.</b> Escolha um plano e tire sua foto de acesso</div>
                    <div><b>4.</b> Pague na hora via Pix (copia e cola)</div>
                    <div><b>5.</b> Baixe o app do aluno pra ver treinos e evolução</div>
                  </div>
                  <div class="foot">${url}</div>
                  <script>window.onload=()=>setTimeout(()=>window.print(),400);</script>
                  </body></html>`;
                const w = window.open("", "_blank", "width=800,height=1000");
                if (!w) return toast.error("Popup bloqueado — libere popups para imprimir");
                w.document.write(html); w.document.close();
              };
              const downloadPng = async () => {
                try {
                  const res = await fetch(qrUrl); const blob = await res.blob();
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(blob); link.download = "americanfit-autocadastro-qr.png";
                  document.body.appendChild(link); link.click(); link.remove();
                  setTimeout(()=>URL.revokeObjectURL(link.href), 1000);
                  toast.success("PNG baixado");
                } catch { toast.error("Falha ao baixar PNG"); }
              };
              return (
                <div className="grid md:grid-cols-[auto,1fr] gap-6 items-start">
                  <div className="flex flex-col items-center">
                    <div className="p-3 border-4 border-rose-600 rounded-2xl bg-white">
                      <img src={qrUrl} alt="QR Autocadastro" className="w-56 h-56" data-testid="signup-qr-img"/>
                    </div>
                    <div className="mt-3 px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-wider">Novo Aluno · Cadastro + Pix</div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label>Link do autocadastro</Label>
                      <div className="mt-1 p-3 bg-slate-50 rounded-lg text-xs font-mono break-all text-slate-700 border border-slate-200" data-testid="signup-qr-url">{url}</div>
                    </div>
                    <div className="text-xs text-slate-500 leading-relaxed">
                      <strong className="text-slate-700">Como usar:</strong> Cole o cartaz A4 na recepção/parede. Novos alunos escaneiam, preenchem dados + endereço + foto facial e pagam via <b>Pix copia e cola</b>. Ao concluir, são direcionados a instalar o app do portal do aluno.
                    </div>
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button variant="outline" onClick={() => window.open(url, "_blank")} data-testid="signup-qr-open"><UserPlus className="w-4 h-4 mr-2"/>Abrir tela</Button>
                      <Button variant="outline" onClick={()=>{navigator.clipboard.writeText(url); toast.success("Link copiado");}} data-testid="signup-qr-copy"><Copy className="w-4 h-4 mr-2"/>Copiar link</Button>
                      <Button onClick={downloadPng} className="bg-slate-900 hover:bg-black text-white font-bold uppercase" data-testid="signup-qr-download"><Download className="w-4 h-4 mr-2"/>Baixar PNG</Button>
                      <Button onClick={doPrint} className="bg-rose-600 hover:bg-rose-700 text-white font-bold uppercase" data-testid="signup-qr-print"><Printer className="w-4 h-4 mr-2"/>Imprimir cartaz A4</Button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </Card>

          {/* ==== QR GENÉRICO DA ACADEMIA ==== */}
          <Card className="p-5 border-slate-200" data-testid="portal-qr-generic-card">
            <div className="flex items-center gap-2 mb-4">
              <QrCode className="w-5 h-5 text-emerald-600"/>
              <h3 className="font-heading text-base font-bold text-slate-900">QR Code do Portal (Recepção / Parede)</h3>
              <span className="text-xs text-slate-500">QR sem CPF — imprima e cole na parede/recepção para qualquer aluno escanear</span>
            </div>
            {(() => {
              const url = `${window.location.origin}/portal/login`;
              const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=10&data=${encodeURIComponent(url)}`;
              const doPrint = () => {
                const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>QR Portal — American Fit</title>
                  <style>@page{size:A4;margin:0}html,body{margin:0;padding:0}
                  body{font-family:'Segoe UI',Arial,sans-serif;width:210mm;min-height:297mm;padding:20mm 15mm;text-align:center;box-sizing:border-box;background:#fff;color:#0f172a}
                  .badge{display:inline-block;background:#10b981;color:#fff;font-size:14px;font-weight:800;letter-spacing:3px;padding:8px 18px;border-radius:999px;text-transform:uppercase;margin-bottom:14px}
                  h1{font-size:56px;letter-spacing:2px;margin:0 0 6px;font-weight:900;text-transform:uppercase}
                  .sub{font-size:22px;color:#475569;margin:0 0 28px;font-weight:600}
                  .qr-wrap{display:inline-block;padding:18px;border:6px solid #0f172a;border-radius:24px;background:#fff}
                  img{display:block;width:150mm;height:150mm}
                  .cta{margin-top:32px;font-size:34px;font-weight:900;color:#0f172a;text-transform:uppercase;letter-spacing:1px}
                  .cta em{font-style:normal;color:#10b981}
                  .steps{margin-top:22px;font-size:16px;color:#334155;line-height:1.7}
                  .steps b{color:#0f172a}
                  .foot{margin-top:36px;padding-top:18px;border-top:2px dashed #cbd5e1;font-size:12px;color:#64748b;word-break:break-all}
                  @media print{body{padding:15mm 10mm}}</style></head><body>
                  <div class="badge">Portal do Aluno</div>
                  <h1>American Fit</h1>
                  <div class="sub">Treinos, dieta com IA, evolução e muito mais 💪</div>
                  <div class="qr-wrap"><img src="${qrUrl}" alt="QR Portal"/></div>
                  <div class="cta">Aponte a câmera e <em>acesse agora</em></div>
                  <div class="steps">
                    <div><b>1.</b> Abra a câmera do celular</div>
                    <div><b>2.</b> Aponte para o QR Code acima</div>
                    <div><b>3.</b> Toque no link e faça login com seu CPF</div>
                  </div>
                  <div class="foot">${url}</div>
                  <script>window.onload=()=>setTimeout(()=>window.print(),400);</script>
                  </body></html>`;
                const w = window.open("", "_blank", "width=800,height=1000");
                if (!w) return toast.error("Popup bloqueado — libere popups para imprimir");
                w.document.write(html); w.document.close();
              };
              const downloadPng = async () => {
                try {
                  const res = await fetch(qrUrl);
                  const blob = await res.blob();
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(blob);
                  link.download = "americanfit-portal-qr.png";
                  document.body.appendChild(link); link.click(); link.remove();
                  setTimeout(()=>URL.revokeObjectURL(link.href), 1000);
                  toast.success("PNG baixado");
                } catch { toast.error("Falha ao baixar PNG"); }
              };
              return (
                <div className="grid md:grid-cols-[auto,1fr] gap-6 items-start">
                  <div className="flex flex-col items-center">
                    <div className="p-3 border-4 border-slate-900 rounded-2xl bg-white">
                      <img src={qrUrl} alt="QR Portal Genérico" className="w-56 h-56" data-testid="portal-qr-generic-img"/>
                    </div>
                    <div className="mt-3 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider">Sem CPF · Uso geral</div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label>Link do portal</Label>
                      <div className="mt-1 p-3 bg-slate-50 rounded-lg text-xs font-mono break-all text-slate-700 border border-slate-200" data-testid="portal-qr-generic-url">{url}</div>
                    </div>
                    <div className="text-xs text-slate-500 leading-relaxed">
                      <strong className="text-slate-700">Como usar:</strong> Imprima em A4, plastifique e cole na recepção ou parede da academia. Qualquer aluno pode escanear para abrir o portal e fazer login com CPF + senha.
                    </div>
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button variant="outline" onClick={()=>{navigator.clipboard.writeText(url); toast.success("Link copiado");}} data-testid="portal-qr-generic-copy"><Copy className="w-4 h-4 mr-2"/>Copiar link</Button>
                      <Button onClick={downloadPng} className="bg-slate-900 hover:bg-black text-white font-bold uppercase" data-testid="portal-qr-generic-download"><Download className="w-4 h-4 mr-2"/>Baixar PNG</Button>
                      <Button onClick={doPrint} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase" data-testid="portal-qr-generic-print"><Printer className="w-4 h-4 mr-2"/>Imprimir cartaz A4</Button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </Card>
        </TabsContent>

        {/* ===================== TAB: ADMIN & PIX ===================== */}
        <TabsContent value="totem" className="space-y-4">
          <TotemLinks />
        </TabsContent>

        <TabsContent value="admin" className="space-y-4">
          <Card className="p-5 border-slate-200" data-testid="admin-config-card">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-rose-600"/>
              <h3 className="font-heading text-base font-bold text-slate-900">Configuração do Admin</h3>
              <span className="text-xs text-slate-500">Usado pelo PDV (QR Pix) e para receber o fechamento do caixa via WhatsApp</span>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Nome do administrador</Label>
                <Input value={adminCfg.admin_name} onChange={(e)=>setAdminCfg({...adminCfg, admin_name: e.target.value})} placeholder="Ex: Carlos Silva" data-testid="admin-name-input"/>
              </div>
              <div>
                <Label>WhatsApp do admin (com DDD)</Label>
                <Input value={adminCfg.admin_phone} onChange={(e)=>setAdminCfg({...adminCfg, admin_phone: maskPhone(e.target.value)})} placeholder="(11) 99999-9999" data-testid="admin-phone-input"/>
              </div>
              <div>
                <Label>Chave Pix (opcional)</Label>
                <Input value={adminCfg.pix_key} onChange={(e)=>setAdminCfg({...adminCfg, pix_key: e.target.value})} placeholder="CNPJ, celular, email ou aleatória" data-testid="admin-pix-key-input"/>
              </div>
            </div>
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div>
                <Label>QR Code Pix (imagem gerada pelo seu app do banco)</Label>
                <div className="mt-2 flex items-start gap-3">
                  <div className="w-40 h-40 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
                    {adminCfg.pix_qr_image ? (
                      <img src={adminCfg.pix_qr_image} alt="QR Pix" className="w-full h-full object-contain" data-testid="admin-pix-qr-preview"/>
                    ) : (
                      <QrCode className="w-10 h-10 text-slate-300"/>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <label className="inline-flex items-center gap-2 cursor-pointer bg-slate-900 text-white px-3 py-2 rounded-lg text-sm font-bold uppercase hover:bg-black" data-testid="admin-pix-qr-upload">
                      <Upload className="w-4 h-4"/>Escolher imagem
                      <input type="file" accept="image/*" className="hidden" onChange={uploadPixQr}/>
                    </label>
                    {adminCfg.pix_qr_image && (
                      <Button variant="outline" size="sm" onClick={()=>setAdminCfg({...adminCfg, pix_qr_image: ""})} data-testid="admin-pix-qr-clear"><Trash2 className="w-4 h-4 mr-1"/>Remover</Button>
                    )}
                    <p className="text-xs text-slate-500">Faça o QR do valor combinado no app do seu banco e envie o print aqui — o PDV mostrará essa imagem quando a forma for Pix.</p>
                  </div>
                </div>
              </div>
              <div>
                <Label>Código Copia-e-Cola Pix (opcional)</Label>
                <textarea rows={6} value={adminCfg.pix_code} onChange={(e)=>setAdminCfg({...adminCfg, pix_code: e.target.value})} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-xs font-mono" placeholder="00020126...5204..." data-testid="admin-pix-code-input"/>
                {adminCfg.pix_code && (
                  <Button variant="outline" size="sm" onClick={()=>{ navigator.clipboard.writeText(adminCfg.pix_code); toast.success("Código copiado"); }} className="mt-2"><Copy className="w-4 h-4 mr-1"/>Testar copiar</Button>
                )}
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={saveAdmin} disabled={adminSaving} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="admin-config-save">
                <Save className="w-4 h-4 mr-2"/>{adminSaving ? "Salvando..." : "Salvar configuração"}
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* ===================== TAB: INTEGRAÇÕES ===================== */}
        <TabsContent value="integrations" className="space-y-4">
          <NextFitIntegrationCard/>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ===================== Card: Integração Next Fit =====================
function NextFitIntegrationCard() {
  return (
    <Card className="p-5 border-slate-200" data-testid="nextfit-integration-card">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-rose-50 flex items-center justify-center">
            <Plug className="w-6 h-6 text-rose-600"/>
          </div>
          <div>
            <h3 className="font-heading text-lg font-black text-slate-900">Integração Next Fit</h3>
            <p className="text-xs text-slate-500 max-w-md">
              A Integração Next Fit agora é <strong>por unidade</strong>. Cada academia tem sua própria credencial e seu histórico próprio.
            </p>
          </div>
        </div>
        <a
          href="/migracao-nextfit"
          className="inline-flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold uppercase text-xs rounded-lg transition-colors"
          data-testid="goto-migration-link"
        >
          <Plug className="w-4 h-4"/>Abrir Central de Migração
        </a>
      </div>
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
        <ul className="list-disc list-inside space-y-0.5">
          <li>Configure uma chave por unidade (criptografada, nunca exposta em logs ou telas).</li>
          <li>Teste a conexão, analise e migre uma unidade de cada vez.</li>
          <li>Histórico, contadores e status de migração ficam separados por unidade.</li>
        </ul>
      </div>
    </Card>
  );
}
